import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { chakra, Input, Flex } from '@chakra-ui/react';
import { FormProvider, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams, useRouteLoaderData } from 'react-router-dom';
import { api, CompanyDto, UpdateCompanyDto } from '../../lib/api';
import { CreateCompanyDto } from '../../lib/api';
import { addressValidationSchema } from '../../validations/address';
import { accreditationBodies } from '../../utils/coding-details';
import Address from '../../components/address';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { toaster } from '@/components/ui/toaster';
import { Card } from '@/components/ui/card';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const defaultValues: CreateCompanyDto = {
    name: '',
    address: {
        address: '',
        city: '',
        state: '',
        zipcode: '',
    },
    agencyId: '',
    taxId: '',
    npi: '',
    ptan: '',
    accreditationBody: '',
    accreditationId: '',
};

export function CompanyPage() {
    const navigate = useNavigate();
    const data = useRouteLoaderData('company-details') as CompanyDto;
    const { companyId } = useParams<'companyId'>();
    const [checkedItems, setCheckedItems] = useState([...Array(6)].map(() => false));
    const allChecked = checkedItems.every(Boolean);
    const isIndeterminate = checkedItems.some(Boolean) && !allChecked;

    // save data
    const { mutate, isPending } = useMutation<unknown, { error: Error }, UpdateCompanyDto | CreateCompanyDto>({
        mutationFn: async (data) => {
            if (companyId) {
                return api.company.update(companyId, data as UpdateCompanyDto);
            }
            return api.company.create(data as CreateCompanyDto);
        },
        onSuccess: () => {
            navigate('/organization/overview');
        },
        onError: (error) => {
            toaster.create({
                title: `Unable to create company: ${error.error.message}`,
                type: 'error',
                duration: 3000,
            });
        },
    });

    // handlers
    const onSubmit: SubmitHandler<CreateCompanyDto> = async (data) => {
        mutate(data);
    };

    // form values and validations

    const validationSchema: Resolver<CreateCompanyDto> = zodResolver(
        z.object({
            name: z.string().min(1, 'required'),
            taxId: z.string().min(1, 'required'),
            npi: z.string().min(1, 'required'),
            ptan: z.string().min(1, 'required'),
            address: addressValidationSchema,
        }),
    );

    const rhf = useForm<CreateCompanyDto>({
        defaultValues: { ...defaultValues, ...data },
        resolver: validationSchema,
        reValidateMode: 'onBlur',
    });

    const {
        formState: { errors },
        register,
    } = rhf;

    return (
        <Flex gap="8" flexDirection="column" maxW="600px">
            <FormProvider {...rhf}>
                <chakra.form noValidate onSubmit={rhf.handleSubmit(onSubmit)} w="100%">
                    <Flex flexDirection="column" gap="24px">
                        <Card type="standard" barTitle="Company Details" w="100%">
                            <Field label="Company name" required invalid={!!errors?.name} errorText={errors?.name?.message}>
                                <Input type="text" {...register('name')} />
                            </Field>
                            <Address
                                isRequired={true}
                                validAddress={!!errors?.address?.address}
                                validCity={!!errors?.address?.city}
                                validState={!!errors?.address?.state}
                                validZipCode={!!errors?.address?.zipcode}
                                errorAddress={errors?.address?.address?.message}
                                errorCity={errors?.address?.city?.message}
                                errorState={errors?.address?.state?.message}
                                errorZipCode={errors?.address?.zipcode?.message}
                                nameAddress="address.address"
                                nameCity="address.city"
                                nameState="address.state"
                                nameZipCode="address.zipcode"
                            />
                        </Card>
                        <Card type="standard" barTitle="Licenses" w="100%">
                            <FieldGroup>
                                <FieldRow>
                                    <Field label="State Agency ID">
                                        <Input type="text" {...register('agencyId')} />
                                    </Field>
                                    <Field label="Federal Tax ID" required invalid={!!errors?.taxId} errorText={errors?.taxId?.message}>
                                        <Input type="text" {...register('taxId')} maxLength={9} data-testid="input-companyTaxId" />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field label="NPI" required invalid={!!errors?.npi} errorText={errors?.npi?.message}>
                                        <Input type="text" {...register('npi')} maxLength={10} data-testid="input-companyNpi" />
                                    </Field>
                                    <Field label="PTAN" required invalid={!!errors?.ptan} errorText={errors?.ptan?.message}>
                                        <Input type="text" {...register('ptan')} maxLength={6} data-testid="input-companyPtan" />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field label="Accreditation Body">
                                        <NativeSelectRoot>
                                            <NativeSelectField {...register('accreditationBody')}>
                                                {accreditationBodies.map((body) => {
                                                    return <option key={body}>{body}</option>;
                                                })}
                                            </NativeSelectField>
                                        </NativeSelectRoot>
                                    </Field>
                                    <Field label="Accreditation ID">
                                        <Input type="text" {...register('accreditationId')} id="accreditationId" name="accreditationId" />
                                    </Field>
                                </FieldRow>
                            </FieldGroup>
                        </Card>
                        <Card type="standard" barTitle="Software Features" w="100%">
                            <FieldGroup>
                                <Checkbox
                                    checked={isIndeterminate ? 'indeterminate' : allChecked}
                                    onChange={(e) => setCheckedItems([...Array(6)].map(() => (e.target as HTMLInputElement).checked))}>
                                    Home Healthcare
                                </Checkbox>
                                <FieldGroup ml="24px" gap="8px">
                                    <Checkbox
                                        checked={checkedItems[0]}
                                        onChange={(e) =>
                                            setCheckedItems([
                                                (e.target as HTMLInputElement).checked,
                                                checkedItems[1],
                                                checkedItems[2],
                                                checkedItems[3],
                                                checkedItems[4],
                                                checkedItems[5],
                                            ])
                                        }>
                                        Skilled Nursing
                                    </Checkbox>
                                    <Checkbox
                                        checked={checkedItems[1]}
                                        onChange={(e) =>
                                            setCheckedItems([
                                                checkedItems[0],
                                                (e.target as HTMLInputElement).checked,
                                                checkedItems[2],
                                                checkedItems[3],
                                                checkedItems[4],
                                                checkedItems[5],
                                            ])
                                        }>
                                        Medical Social Worker
                                    </Checkbox>
                                    <Checkbox
                                        checked={checkedItems[2]}
                                        onChange={(e) =>
                                            setCheckedItems([
                                                checkedItems[0],
                                                checkedItems[1],
                                                (e.target as HTMLInputElement).checked,
                                                checkedItems[3],
                                                checkedItems[4],
                                                checkedItems[5],
                                            ])
                                        }>
                                        Home Health Aide
                                    </Checkbox>
                                    <Checkbox
                                        checked={checkedItems[3]}
                                        onChange={(e) =>
                                            setCheckedItems([
                                                checkedItems[0],
                                                checkedItems[1],
                                                checkedItems[2],
                                                (e.target as HTMLInputElement).checked,
                                                checkedItems[4],
                                                checkedItems[5],
                                            ])
                                        }>
                                        Physical Therapy
                                    </Checkbox>
                                    <Checkbox
                                        checked={checkedItems[4]}
                                        onChange={(e) =>
                                            setCheckedItems([
                                                checkedItems[0],
                                                checkedItems[1],
                                                checkedItems[2],
                                                checkedItems[3],
                                                (e.target as HTMLInputElement).checked,
                                                checkedItems[5],
                                            ])
                                        }>
                                        Speech Therapay
                                    </Checkbox>
                                    <Checkbox
                                        checked={checkedItems[5]}
                                        onChange={(e) =>
                                            setCheckedItems([
                                                checkedItems[0],
                                                checkedItems[1],
                                                checkedItems[2],
                                                checkedItems[3],
                                                checkedItems[4],
                                                (e.target as HTMLInputElement).checked,
                                            ])
                                        }>
                                        Occupational Therapay
                                    </Checkbox>
                                </FieldGroup>
                                <Checkbox>Hospice</Checkbox>
                                <Checkbox>Personal Care Aide (PCA)</Checkbox>
                            </FieldGroup>
                        </Card>
                        <Flex>
                            <Button type="submit" disabled={rhf.formState.isSubmitting} loadingText="saving" loading={isPending}>
                                {data ? 'Save' : 'Create'}
                            </Button>
                        </Flex>
                    </Flex>
                </chakra.form>
            </FormProvider>
        </Flex>
    );
}
